import { memo, useState, useRef } from 'react';
import {
  Button,
  Flex,
  Text,
  Popover,
  PopoverTrigger,
  Portal,
  PopoverContent,
  Box,
  Image,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { MdHelp, MdDescription, MdSettings } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';

import ContactSupportIcon from 'components/Icons/ContactSupportIcon';
import LogoutIcon from 'components/Icons/LogoutIcon';
import UserProfileIcon from 'components/Icons/UserProfileIcon';
import ShareButtonWithPopup from 'components/Header/ShareButtonWithPopup';
import ShareIcon from 'components/Icons/ShareIcon';
import LightFilledUserAvatar from 'components/Icons/FilledLightUserAvatar';
import LogoutModal from 'components/LogoutModal';
import TextComponent from 'components/Text';
import { getUserData, getUserSubscriptionLimits, SetUserModalVisible } from 'redux/UserSlice';

const getInitials = (firstname?: string, lastname?: string) => {
  const first = firstname?.charAt(0)?.toUpperCase() || '';
  const last = lastname?.charAt(0)?.toUpperCase() || '';
  return `${first}${last}` || 'U';
};

const UserActions = memo(() => {
  const dispatch: any = useDispatch();
  const userData = useSelector(getUserData);
  const userSubscriptionLimits = useSelector(getUserSubscriptionLimits);
  const [logoutModal, setLogoutModal] = useState(false);
  const shareButtonReference = useRef<HTMLDivElement>(null);

  const handleMenuItemClick = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <>
      <Flex alignItems='center' gap={1} flexShrink={0} width='auto'>
        <Menu>
          {({ isOpen }) => {
            return (
              <>
                <MenuButton
                  as={Button}
                  variant='solid'
                  bg='white'
                  borderRadius='full'
                  width='40px'
                  height='40px'
                  minW='40px'
                  border='1px solid #D9D9D9'
                  p={0}
                  _hover={{ bg: 'gray.50' }}
                  aria-label='Help'
                >
                  <Icon as={MdHelp} color='#745BE3' boxSize={5} />
                </MenuButton>
                {isOpen && (
                  <MenuList
                    zIndex={9}
                    minW='200px'
                    py={0}
                    borderRadius='xl'
                    boxShadow='0px 4px 12px rgba(0, 0, 0, 0.08)'
                    border='1px solid'
                    borderColor='gray.200'
                  >
                    <MenuItem
                      py={3}
                      px={4}
                      onClick={() => handleMenuItemClick('https://help.blox.so/')}
                      _hover={{ bg: 'gray.50' }}
                      borderBottom='1px solid'
                      borderColor='gray.100'
                    >
                      <Flex alignItems='center' gap={3}>
                        <Box
                          boxSize={8}
                          borderRadius='full'
                          bg='#F1EEFF'
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                        >
                          <Icon as={MdHelp} color='#745BE3' boxSize={4} />
                        </Box>
                        <Text fontWeight='500' color='black'>
                          Help Center
                        </Text>
                      </Flex>
                    </MenuItem>
                    <MenuItem
                      py={3}
                      px={4}
                      onClick={() => {}}
                      _hover={{ bg: 'gray.50' }}
                      borderBottom='1px solid'
                      borderColor='gray.100'
                    >
                      <Flex alignItems='center' gap={3}>
                        <Box
                          boxSize={8}
                          borderRadius='full'
                          bg='#F1EEFF'
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                        >
                          <Icon as={MdDescription} color='#745BE3' boxSize={4} />
                        </Box>
                        <Text fontWeight='500' color='black'>
                          What's New
                        </Text>
                      </Flex>
                    </MenuItem>
                    <MenuItem
                      py={3}
                      px={4}
                      onClick={() => handleMenuItemClick('https://www.blox.so/contact')}
                      _hover={{ bg: 'gray.50' }}
                    >
                      <Flex alignItems='center' gap={3}>
                        <Box
                          boxSize={8}
                          borderRadius='full'
                          bg='#F1EEFF'
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                        >
                          <ContactSupportIcon width={16} height={16} color='#745BE3' />
                        </Box>
                        <Text fontWeight='500' color='black'>
                          Contact Support
                        </Text>
                      </Flex>
                    </MenuItem>
                  </MenuList>
                )}
              </>
            );
          }}
        </Menu>

        <Tooltip label='Share modal' hasArrow placement='bottom'>
          <Button
            variant='solid'
            bg='#F1EEFF'
            borderRadius='12px'
            border='1px solid #E0DAFE'
            height='40px'
            minW='40px'
            width='fit-content'
            p={0}
            _hover={{ bg: 'gray.50' }}
            aria-label='Share modal'
            onClick={() => {
              // Trigger ShareButtonWithPopup's button click
              const shareButton = shareButtonReference.current?.querySelector('button');
              if (shareButton) {
                shareButton.click();
              }
            }}
          >
            <Flex alignItems='center' justifyContent='center' gap={2} px={2}>
              <ShareIcon width={20} height={20} color='#6B5FC6' />
              <Text fontSize='sm' fontWeight='600' color='black'>
                Share Model
              </Text>
            </Flex>
          </Button>
        </Tooltip>

        <Box
          ref={shareButtonReference}
          position='absolute'
          visibility='hidden'
          pointerEvents='none'
        >
          <ShareButtonWithPopup />
        </Box>

        <Popover isLazy gutter={16} placement='bottom-end'>
          {({ isOpen }) => (
            <>
              <PopoverTrigger>
                <Button
                  variant='solid'
                  bg='white'
                  borderRadius='full'
                  width='40px'
                  height='40px'
                  minW='40px'
                  border='1px solid #D9D9D9'
                  p={0}
                  _hover={{ bg: 'gray.50' }}
                  aria-label='Open user menu'
                >
                  <Text fontSize='sm' fontWeight='600' color='black'>
                    {getInitials(userData?.firstname, userData?.lastname)}
                  </Text>
                </Button>
              </PopoverTrigger>
              {isOpen && (
                <Portal>
                  <PopoverContent
                    position='absolute'
                    right='0'
                    p={0}
                    w='fit-content'
                    minWidth='280px'
                    color='black'
                    borderWidth='1px'
                    borderRadius='8px'
                    borderColor='gray.200'
                    bg='white'
                    boxShadow='0px 4px 12px rgba(0, 0, 0, 0.08)'
                    mr='2'
                  >
                    <Box
                      as='button'
                      w='100%'
                      textAlign='left'
                      p={4}
                      onClick={() => dispatch(SetUserModalVisible(true))}
                      _hover={{ bg: 'gray.50' }}
                      borderRadius='xl'
                      transition='background 0.15s'
                    >
                      <Flex alignItems='center' gap={4}>
                        {userData?.profile_image_url ? (
                          <Image
                            boxShadow='1px 1px 10px #d9d9d9'
                            src={userData?.profile_image_url}
                            borderRadius='16px'
                            width='56px'
                            height='56px'
                            flexShrink={0}
                            objectFit='cover'
                          />
                        ) : (
                          <LightFilledUserAvatar />
                        )}
                        <Box flex={1} overflow='hidden' minW={0}>
                          <Tooltip label={`${userData?.firstname} ${userData?.lastname}`}>
                            <Box>
                              <TextComponent
                                ellipsis={true}
                                as='h2'
                                fontWeight='700'
                                fontSize='lg'
                                color='black'
                              >
                                {`${userData?.firstname} ${userData?.lastname}`}
                              </TextComponent>
                            </Box>
                          </Tooltip>
                          <Text fontSize='sm' color='gray.500' mt={0.5}>
                            {userSubscriptionLimits?.current_subscription_name}
                          </Text>
                        </Box>
                        <ChevronRightIcon boxSize={5} color='black' flexShrink={0} />
                      </Flex>
                    </Box>
                    <Box borderTop='1px solid' borderColor='gray.100' px={3} py={3}>
                      <Flex gap={2} justifyContent='center'>
                        <Tooltip label='Profile' hasArrow placement='top'>
                          <Button
                            aria-label='Profile'
                            size='sm'
                            bg='#F1EEFF'
                            borderRadius='8px'
                            width='63px'
                            height='46px'
                            minW='63px'
                            p={'8px'}
                            _hover={{ bg: '#E0DAFE' }}
                            onClick={() => dispatch(SetUserModalVisible(true))}
                          >
                            <UserProfileIcon width={20} height={20} color='#745BE3' />
                          </Button>
                        </Tooltip>
                        <Tooltip label='Settings' hasArrow placement='top'>
                          <Button
                            aria-label='Settings'
                            size='sm'
                            bg='#F1EEFF'
                            borderRadius='8px'
                            width='63px'
                            height='46px'
                            minW='63px'
                            p={'8px'}
                            _hover={{ bg: '#E0DAFE' }}
                            onClick={() => dispatch(SetUserModalVisible(true))}
                          >
                            <Icon as={MdSettings} color='#745BE3' boxSize={5} />
                          </Button>
                        </Tooltip>
                        <Tooltip label='Log out' hasArrow placement='top'>
                          <Button
                            aria-label='Log out'
                            size='sm'
                            bg='#F1EEFF'
                            borderRadius='8px'
                            width='63px'
                            height='46px'
                            minW='63px'
                            p={'8px'}
                            _hover={{ bg: '#E0DAFE' }}
                            onClick={() => setLogoutModal(true)}
                          >
                            <LogoutIcon width={20} height={20} color='#745BE3' />
                          </Button>
                        </Tooltip>
                      </Flex>
                    </Box>
                  </PopoverContent>
                </Portal>
              )}
            </>
          )}
        </Popover>
      </Flex>

      {logoutModal && (
        <LogoutModal
          isOpen={logoutModal}
          onClose={() => setLogoutModal(false)}
          onOpenAuthModal={() => {}}
        />
      )}
    </>
  );
});

UserActions.displayName = 'UserActions';

export default UserActions;
